(() => {
  const SELECTORS = {
    card: '[data-bennett-finish-card]',
    group: '[data-finish-group]',
    filter: '[data-finish-filter]',
    search: '[data-finish-search-input]',
    clear: '[data-finish-clear]',
    reset: '[data-finish-reset]',
    empty: '[data-finish-empty]',
    resultCount: '[data-finish-result-count]',
    compare: '[data-finish-compare]',
    compareCount: '[data-finish-compare-count]',
    rail: '[data-finish-rail]',
    track: '[data-finish-track]',
    scrollButton: '[data-finish-scroll]',
    expand: '[data-finish-expand]',
    editorialFeature: '.bennett-finish-feature'
  };

  const state = {
    category: 'all',
    query: '',
    compared: new Set()
  };

  const validCategories = new Set(['all', 'painted', 'stains', 'veneers', 'burl', 'gab', 'new']);
  const requestedCategory = new URLSearchParams(window.location.search).get('category');
  if (validCategories.has(requestedCategory)) state.category = requestedCategory;

  const normalize = (value) => (value || '').toLowerCase().trim();

  const getCards = () => Array.from(document.querySelectorAll(SELECTORS.card));

  const updateEditorialFeatures = () => {
    const filtering = state.category !== 'all' || Boolean(state.query);
    document.querySelectorAll(SELECTORS.editorialFeature).forEach((feature) => {
      feature.hidden = filtering;
    });
  };

  const matchesCategory = (card) => {
    if (state.category === 'all') return true;
    if (state.category === 'new') return card.dataset.finishNew === 'true';
    return card.dataset.finishCategory === state.category;
  };

  const applyFilters = () => {
    const cards = getCards();
    let visibleCount = 0;

    cards.forEach((card) => {
      const matchesSearch = !state.query || normalize(card.dataset.finishSearch).includes(state.query);
      const visible = matchesSearch && matchesCategory(card);
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    document.querySelectorAll(SELECTORS.group).forEach((group) => {
      const hasVisibleCards = Array.from(group.querySelectorAll(SELECTORS.card)).some((card) => !card.hidden);
      group.hidden = !hasVisibleCards;
    });

    document.querySelectorAll(SELECTORS.resultCount).forEach((node) => {
      node.textContent = String(visibleCount);
    });
    document.querySelectorAll(SELECTORS.empty).forEach((node) => {
      node.hidden = visibleCount !== 0;
    });
    document.querySelectorAll(SELECTORS.clear).forEach((node) => {
      node.hidden = !state.query;
    });
    updateEditorialFeatures();
    document.querySelectorAll(SELECTORS.rail).forEach(updateRailButtons);
  };

  const updateCompareCount = () => {
    document.querySelectorAll(SELECTORS.compareCount).forEach((node) => {
      node.textContent = String(state.compared.size);
    });
  };

  const setGroupExpanded = (group, expanded) => {
    group.classList.toggle('is-expanded', expanded);
    group.querySelectorAll(SELECTORS.expand).forEach((button) => {
      button.setAttribute('aria-expanded', String(expanded));
      const label = button.querySelector('[data-finish-expand-label]');
      if (label) label.textContent = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel;
    });
    const rail = group.querySelector(SELECTORS.rail);
    if (rail) updateRailButtons(rail);
  };

  const syncExpandedCategory = (scrollToGroup = false) => {
    const groupMap = {
      painted: 'painted',
      stains: 'stains',
      veneers: 'veneers',
      burl: 'veneers',
      gab: 'special',
      metal: 'special'
    };
    const targetHandle = groupMap[state.category];
    let targetGroup;

    document.querySelectorAll(SELECTORS.group).forEach((group) => {
      const shouldExpand = state.category === 'new' ? !group.hidden : group.dataset.groupHandle === targetHandle;
      setGroupExpanded(group, Boolean(shouldExpand));
      if (shouldExpand && targetHandle) targetGroup = group;
    });

    if (scrollToGroup && targetGroup) {
      requestAnimationFrame(() => targetGroup.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  const updateStickyOffset = () => {
    const header = document.querySelector('.section-header');
    const offset = header ? header.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--bennett-finish-sticky-offset', `${Math.max(0, offset)}px`);
  };

  const resetFilters = () => {
    state.category = 'all';
    state.query = '';
    document.querySelectorAll(SELECTORS.search).forEach((input) => {
      input.value = '';
    });
    document.querySelectorAll(SELECTORS.filter).forEach((button) => {
      const isActive = button.dataset.finishFilter === 'all';
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    applyFilters();
    syncExpandedCategory();
  };

  const syncFilterButtons = () => {
    document.querySelectorAll(SELECTORS.filter).forEach((button) => {
      const isActive = button.dataset.finishFilter === state.category;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  };

  const bindControls = (root) => {
    if (root.dataset.bennettInitialized === 'true') return;
    root.dataset.bennettInitialized = 'true';

    root.querySelectorAll(SELECTORS.filter).forEach((button) => {
      button.addEventListener('click', () => {
        state.category = button.dataset.finishFilter;
        document.querySelectorAll(SELECTORS.filter).forEach((filterButton) => {
          const isActive = filterButton === button;
          filterButton.classList.toggle('is-active', isActive);
          filterButton.setAttribute('aria-pressed', String(isActive));
        });
        applyFilters();
        syncExpandedCategory(true);
      });
    });

    root.querySelectorAll(SELECTORS.search).forEach((input) => {
      input.addEventListener('input', () => {
        state.query = normalize(input.value);
        applyFilters();
      });
    });

    root.querySelectorAll(SELECTORS.clear).forEach((button) => {
      button.addEventListener('click', () => {
        state.query = '';
        root.querySelectorAll(SELECTORS.search).forEach((input) => {
          input.value = '';
          input.focus();
        });
        applyFilters();
      });
    });

    root.querySelectorAll(SELECTORS.reset).forEach((button) => button.addEventListener('click', resetFilters));
  };

  const bindExpanders = (root = document) => {
    root.querySelectorAll(SELECTORS.expand).forEach((button) => {
      if (button.dataset.bennettInitialized === 'true') return;
      button.dataset.bennettInitialized = 'true';
      button.addEventListener('click', () => {
        const group = button.closest(SELECTORS.group);
        if (!group) return;
        setGroupExpanded(group, !group.classList.contains('is-expanded'));
      });
    });
  };

  const bindCards = (root = document) => {
    root.querySelectorAll(SELECTORS.compare).forEach((button) => {
      if (button.dataset.bennettInitialized === 'true') return;
      button.dataset.bennettInitialized = 'true';
      button.addEventListener('click', () => {
        const card = button.closest(SELECTORS.card);
        const id = card?.dataset.finishId;
        if (!id) return;
        if (state.compared.has(id)) state.compared.delete(id);
        else state.compared.add(id);
        const selected = state.compared.has(id);
        button.setAttribute('aria-pressed', String(selected));
        card.classList.toggle('is-compared', selected);
        updateCompareCount();
      });
    });
  };

  const updateRailButtons = (rail) => {
    const track = rail.querySelector(SELECTORS.track);
    if (!track) return;
    const previous = rail.querySelector('[data-finish-scroll="previous"]');
    const next = rail.querySelector('[data-finish-scroll="next"]');
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (previous) previous.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 2 || maxScroll <= 2;
  };

  const bindRails = (root = document) => {
    root.querySelectorAll(SELECTORS.rail).forEach((rail) => {
      if (rail.dataset.bennettInitialized === 'true') return;
      rail.dataset.bennettInitialized = 'true';
      const track = rail.querySelector(SELECTORS.track);
      if (!track) return;
      rail.querySelectorAll(SELECTORS.scrollButton).forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.finishScroll === 'previous' ? -1 : 1;
          track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: 'smooth' });
        });
      });
      track.addEventListener('scroll', () => updateRailButtons(rail), { passive: true });
      updateRailButtons(rail);
    });
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-bennett-finish-library]').forEach((library) => {
      if (library.querySelector('[data-finish-controls]')) bindControls(library);
    });
    bindCards(root);
    bindRails(root);
    bindExpanders(root);
    syncFilterButtons();
    applyFilters();
    syncExpandedCategory(Boolean(requestedCategory));
    updateCompareCount();
    updateStickyOffset();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', applyFilters);
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.querySelector?.(SELECTORS.card);
    card?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  });
  window.addEventListener('resize', () => {
    updateStickyOffset();
    document.querySelectorAll(SELECTORS.rail).forEach(updateRailButtons);
  }, { passive: true });
})();
